import { CustomDriveStep } from '../models/customDriverSteps';

const selectCatalogueTab = async (
  tabSelector: string,
  contentSelector: string,
  targetSelector: string,
): Promise<void> => {
  const tab = document.querySelector(tabSelector);
  if (!(tab instanceof HTMLElement)) return;

  const isActive =
    tab.getAttribute('aria-selected') === 'true' || tab.classList.contains('mdc-tab--active');
  if (!isActive) tab.click();

  await new Promise<void>(resolve => setTimeout(resolve, 50));

  const timeout = Date.now() + 10000;
  let previousLayout = '';
  let stableFrames = 0;
  while (Date.now() < timeout) {
    const content = document.querySelector(contentSelector);
    const table = content?.querySelector('table');
    const paginator = content?.parentElement?.querySelector('mat-paginator');
    const activeTabBody = content?.closest('.mat-mdc-tab-body-active');
    const loading =
      document.querySelector('.spinner-container') !== null ||
      content?.getAttribute('data-catalogue-loading') === 'true';
    const tableReady =
      content instanceof HTMLElement &&
      table instanceof HTMLElement &&
      activeTabBody !== null &&
      !loading &&
      !!table.querySelector('tbody tr') &&
      table.getBoundingClientRect().height > 0 &&
      (!paginator ||
        (paginator instanceof HTMLElement && paginator.getBoundingClientRect().height > 0));

    if (tableReady) {
      const tableRect = table.getBoundingClientRect();
      const paginatorRect = paginator?.getBoundingClientRect();
      const layout = [
        tableRect.left,
        tableRect.top,
        tableRect.width,
        tableRect.height,
        paginatorRect?.top ?? 0,
        paginatorRect?.height ?? 0,
      ].join(':');
      stableFrames = layout === previousLayout ? stableFrames + 1 : 0;
      previousLayout = layout;

      if (stableFrames >= 2) {
        const target = document.querySelector(targetSelector);
        const tab = document.querySelector(tabSelector);
        if (target instanceof HTMLElement && tab instanceof HTMLElement) {
          const tabRect = tab.getBoundingClientRect();
          const rects = [tabRect, tableRect, ...(paginatorRect ? [paginatorRect] : [])];
          const left = Math.min(...rects.map(rect => rect.left));
          const top = Math.min(...rects.map(rect => rect.top));
          const right = Math.max(...rects.map(rect => rect.right));
          const bottom = Math.max(...rects.map(rect => rect.bottom));
          Object.assign(target.style, {
            display: 'block',
            position: 'fixed',
            left: `${left}px`,
            top: `${top}px`,
            width: `${right - left}px`,
            height: `${bottom - top}px`,
          });
        }
        return;
      }
    } else {
      stableFrames = 0;
      previousLayout = '';
    }

    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  }
};

export const StepHome: CustomDriveStep[] = [
  {
    element: '#step1',
    popover: {
      title: 'ONBOARDING.HOME.STEP1_TITLE',
      description: 'ONBOARDING.HOME.STEP1_DESC',
      side: 'left',
    },
    closeBackdrop: true,
  },
  {
    element: '#step1',
    popover: {
      title: 'ONBOARDING.HOME.PREP_SIMILAR_TITLE',
      description: 'ONBOARDING.HOME.PREP_SIMILAR_DESC',
    },
    triggerClick: true,
    opensModal: true,
    modalGroup: 'prep-modal',
    afterHighlight: async () => {
      await new Promise(resolve => setTimeout(resolve));
      const buttonElement = document.querySelector('#button_similar_dialog');
      if (buttonElement instanceof HTMLElement) {
        buttonElement.classList.add('driver-additional-highlight');
      }
    },
  },
  {
    element: '#button_lesson_dialog',
    popover: {
      title: 'ONBOARDING.HOME.PREP_LESSONS_TITLE',
      description: 'ONBOARDING.HOME.PREP_LESSONS_DESC',
    },
    modalGroup: 'prep-modal',
  },
  {
    element: '#button_data_dialog',
    popover: {
      title: 'ONBOARDING.HOME.PREP_DATA_TITLE',
      description: 'ONBOARDING.HOME.PREP_DATA_DESC',
    },
    modalGroup: 'prep-modal',
  },
  {
    element: '#button_literature_dialog',
    popover: {
      title: 'ONBOARDING.HOME.PREP_LITERATURE_TITLE',
      description: 'ONBOARDING.HOME.PREP_LITERATURE_DESC',
    },
    modalGroup: 'prep-modal',
  },
  {
    element: '#button_document_dialog',
    popover: {
      title: 'ONBOARDING.HOME.PREP_INSTITUTIONAL_TITLE',
      description: 'ONBOARDING.HOME.PREP_INSTITUTIONAL_DESC',
    },
    modalGroup: 'prep-modal',
  },
  {
    element: '#step2',
    popover: {
      title: 'ONBOARDING.HOME.STEP2_TITLE',
      description: 'ONBOARDING.HOME.STEP2_DESC',
      side: 'left',
    },
    closeBackdrop: true,
  },
  {
    element: '#step2',
    popover: {
      title: 'ONBOARDING.HOME.CREATED_LESSON_TITLE',
      description: 'ONBOARDING.HOME.CREATED_LESSON_DESC',
    },
    triggerClick: true,
    opensModal: true,
    modalGroup: 'exec-modal',
    afterHighlight: async () => {
      await new Promise(resolve => setTimeout(resolve));
      const buttonElement = document.querySelector('#button_created_lesson_dialog');
      if (buttonElement instanceof HTMLElement) {
        buttonElement.classList.add('driver-additional-highlight');
      }
    },
  },
  {
    element: '#step_buckets',
    popover: {
      title: 'ONBOARDING.HOME.PREP_BUCKETS_TITLE',
      description: 'ONBOARDING.HOME.PREP_BUCKETS_DESC',
    },
    modalGroup: 'exec-modal',
  },
  {
    element: '#step-home-sidebar',
    popover: {
      title: 'ONBOARDING.HOME.SIDEBAR_TITLE',
      description: 'ONBOARDING.HOME.SIDEBAR_DESC',
    },
  },
  {
    element: '#step-home-search',
    popover: {
      title: 'ONBOARDING.HOME.SEARCH_TITLE',
      description: 'ONBOARDING.HOME.SEARCH_DESC',
    },
  },
  {
    element: '#step-home-grouping',
    popover: {
      title: 'ONBOARDING.HOME.GROUPING_TITLE',
      description: 'ONBOARDING.HOME.GROUPING_DESC',
    },
  },
  {
    element: '#step-onbarding',
    popover: {
      description: 'ONBOARDING.HOME.STEP_START_DESC',
      html: true,
    },
  },
];

export const StepLessonsLearning: CustomDriveStep[] = [
  {
    element: '#step5',
    popover: {
      title: 'ONBOARDING.LESSONS.LS_TURN_KNOWLEDGE_TITLE',
      description: 'ONBOARDING.LESSONS.LS_TURN_KNOWLEDGE_DESC',
    },
  },
  {
    element: '#step7',
    popover: {
      title: 'ONBOARDING.LESSONS.LS_QUESTIONS_TITLE',
      description: 'ONBOARDING.LESSONS.LS_QUESTIONS_DESC',
    },
  },
  {
    element: '#step8-your-lessons-target',
    popover: {
      title: 'ONBOARDING.LESSONS.LS_YOUR_CATALOG_TITLE',
      description: 'ONBOARDING.LESSONS.LS_YOUR_CATALOG_DESC',
    },
    beforeNavigation: () =>
      selectCatalogueTab(
        '.step8-your-lessons-tab',
        '#step8-your-lessons',
        '#step8-your-lessons-target',
      ),
  },
  {
    element: '#step8-shared-target',
    popover: {
      title: 'ONBOARDING.LESSONS.LS_SHARED_CATALOG_TITLE',
      description: 'ONBOARDING.LESSONS.LS_SHARED_CATALOG_DESC',
    },
    beforeNavigation: () =>
      selectCatalogueTab('.step8-shared-tab', '#step8-shared-content', '#step8-shared-target'),
  },
];

export function getLessonsLearningSteps(isCrossCutting: boolean): CustomDriveStep[] {
  if (!isCrossCutting) {
    return StepLessonsLearning;
  }

  return StepLessonsLearning.filter(step => step.element !== '#step5').map(step =>
    step.element === '#step7'
      ? {
          ...step,
          popover: {
            ...step.popover,
            title: 'ONBOARDING.LESSONS.LS_CROSSCUTTING_TITLE',
            description: 'ONBOARDING.LESSONS.LS_CROSSCUTTING_DESC',
          },
        }
      : step,
  );
}

export const StepLessonsLearningFinished: CustomDriveStep[] = [
  {
    element: '#step16',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_FINISHED_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_FINISHED_DESC',
    },
  },
];

export const StepQuestionForm: CustomDriveStep[] = [
  {
    element: '#step9',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_LESSON_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_LESSON_TITLE_DESC',
    },
  },
  {
    element: '#step10',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_QUESTION_SET_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_QUESTION_SET_DESC',
      side: 'top',
      align: 'start',
    },
  },
];

export const StepHomeOpenSearch: CustomDriveStep[] = [
  {
    element: '#open-search-input',
    popover: {
      title: 'ONBOARDING.OPEN_SEARCH.OS_INPUT_TITLE',
      description: 'ONBOARDING.OPEN_SEARCH.OS_INPUT_DESC',
    },
  },
  {
    element: '#quick-search-button',
    popover: {
      title: 'ONBOARDING.OPEN_SEARCH.OS_QUICKSEARCH_TITLE',
      description: 'ONBOARDING.OPEN_SEARCH.OS_QUICKSEARCH_DESC',
    },
  },
  {
    element: '#recent-search-button',
    popover: {
      title: 'ONBOARDING.OPEN_SEARCH.OS_RECENTSEARCH_TITLE',
      description: 'ONBOARDING.OPEN_SEARCH.OS_RECENTSEARCH_DESC',
    },
  },
  {
    element: '#open-search-button',
    popover: {
      title: 'ONBOARDING.OPEN_SEARCH.OS_BTN_TITLE',
      description: 'ONBOARDING.OPEN_SEARCH.OS_BTN_DESC',
    },
  },
];

export const StepHomeGlobalSearchResults: CustomDriveStep[] = [
  {
    element: '#home-search-results-summary',
    popover: {
      title: 'ONBOARDING.HOME.SEARCH_RESULTS_TITLE',
      description: 'ONBOARDING.HOME.SEARCH_RESULTS_DESC',
    },
    waitForElement: 3000,
  },
  {
    element: '#step-global-search-remote-project',
    popover: {
      title: 'ONBOARDING.HOME.SEARCH_PROJECT_TITLE',
      description: 'ONBOARDING.HOME.SEARCH_PROJECT_DESC',
      side: 'right',
      align: 'start',
      html: true,
    },
    waitForElement: 3000,
    triggerClick: true,
    opensModal: true,
    modalGroup: 'global-search-project-modal',
    afterHighlight: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      const bucketsElement = document.querySelector('#step_buckets');
      if (bucketsElement instanceof HTMLElement) {
        bucketsElement.classList.add('driver-additional-highlight');
      }
    },
  },
];

export const StepOpenSearchResponseView: CustomDriveStep[] = [
  {
    element: '#referenced-pages-button',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.REFERENCED_PAGES_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.REFERENCED_PAGES_DESC',
    },
  },
  {
    element: '#sources-overview-panel',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.SOURCES_OVERVIEW_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.SOURCES_OVERVIEW_DESC',
    },
  },
  {
    element: '#copy-sources-button',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.COPY_SOURCES_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.COPY_SOURCES_DESC',
    },
  },
  {
    element: '#show-sources-overview-button',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.SHOW_SOURCES_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.SHOW_SOURCES_DESC',
    },
  },
  {
    element: '#search-bar-response-view',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.SEARCH_BAR_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.SEARCH_BAR_DESC',
    },
  },
  {
    element: '#copy-answer-sources-button',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.COPY_ANSWER_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.COPY_ANSWER_DESC',
    },
  },
  {
    element: '#feedback-buttons',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.RATE_ANSWERS_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.RATE_ANSWERS_DESC',
    },
  },
  {
    element: '#new-search-button',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.NEW_SEARCH_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.NEW_SEARCH_DESC',
    },
  },
  {
    element: '#shortcuts-button',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.SHORTCUTS_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.SHORTCUTS_DESC',
    },
  },
  {
    element: '#download-response-button',
    popover: {
      title: 'ONBOARDING.SEARCH_RESPONSE.DOWNLOAD_RESPONSE_TITLE',
      description: 'ONBOARDING.SEARCH_RESPONSE.DOWNLOAD_RESPONSE_DESC',
      side: 'top',
    },
    openSelect: true,
    highlightAdditionalElements: ['.cdk-overlay-pane'],
  },
];

export const StepDraftLesson: CustomDriveStep[] = [
  {
    element: '#step12',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_COPY_LESSON_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_COPY_LESSON_DESC',
    },
  },
  {
    element: '#step13',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_SHARE_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_SHARE_DESC',
    },
  },
  {
    element: '#step11',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_EDIT_RESPONSES_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_EDIT_RESPONSES_DESC',
    },
  },
  {
    element: '#step14',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_EDIT_LESSON_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_EDIT_LESSON_DESC',
    },
  },
  {
    element: '#step15',
    popover: {
      title: 'ONBOARDING.LS_QUESTIONS.LS_EDIT_COMPLETE_TITLE',
      description: 'ONBOARDING.LS_QUESTIONS.LS_EDIT_COMPLETE_DESC',
    },
  },
];

export const StepPillsKnowledge: CustomDriveStep[] = [
  {
    element: '#preparation-header',
    popover: {
      title: 'ONBOARDING.PILLS.SP_HEADER_TITLE',
      description: 'ONBOARDING.PILLS.SP_HEADER_DESC',
    },
    closeBackdrop: true,
  },
  {
    element: '#execution-header',
    popover: {
      title: 'ONBOARDING.LESSONS.LS_HEADER_TITLE',
      description: 'ONBOARDING.LESSONS.LS_HEADER_DESC',
    },
    closeBackdrop: true,
  },
  {
    element: '#similar_step_2',
    popover: {
      title: 'ONBOARDING.PILLS.SP_MENU_BUCKETS_TITLE',
      description: 'ONBOARDING.PILLS.SP_MENU_BUCKETS_DESC',
      side: 'left',
    },
    openSelect: true,
    highlightAdditionalElements: ['.cdk-overlay-pane'],
  },
  {
    element: '#similar_step_3',
    popover: {
      title: 'ONBOARDING.PILLS.SP_PILL_INFO_TITLE',
      description: 'ONBOARDING.PILLS.SP_PILL_INFO_DESC',
    },
    closeBackdrop: true,
  },
  {
    element: '#similar_step_4',
    popover: {
      title: 'ONBOARDING.PILLS.SP_REFERENCES_TITLE',
      description: 'ONBOARDING.PILLS.SP_REFERENCES_DESC',
    },
  },
  {
    element: '#similar_step_5',
    popover: {
      title: 'ONBOARDING.PILLS.SP_METADATA_TITLE',
      description: 'ONBOARDING.PILLS.SP_METADATA_',
    },
    closeBackdrop: true,
  },
  {
    element: '#simila_filter',
    popover: {
      title: 'ONBOARDING.PILLS.FILTERS_PILLS_TITLE',
      description: 'ONBOARDING.PILLS.FILTERS_PILLS_DESC',
      side: 'top',
      align: 'end',
    },
    triggerClick: true,
  },
];

export const StepSimilarProject: CustomDriveStep[] = [
  {
    element: '#Open_similar_step_1',
    popover: {
      title: 'ONBOARDING.OPEN_PILLS.OPEN_NAV_TITLE',
      description: 'ONBOARDING.OPEN_PILLS.OPEN_NAV_DESC',
    },
  },
  {
    element: '#Open_similar_copy',
    popover: {
      title: 'ONBOARDING.OPEN_PILLS.OPEN_COPY_TITLE',
      description: 'ONBOARDING.OPEN_PILLS.OPEN_COPY_DESC',
    },
  },
  {
    element: '#Open_similar_step_2',
    popover: {
      title: 'ONBOARDING.OPEN_PILLS.OPEN_SOURCE_TITLE',
      description: 'ONBOARDING.OPEN_PILLS.OPEN_SOURCE_DESC',
    },
    openAccordeon: true,
  },
];
